import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'sonner';
import { useTranslation } from "../contexts/TranslationContext";
import { useForm } from "react-hook-form";
import { emailValidation, passwordValidation, passwordConfirmValidation } from "../utils/validation.js";
import { Loader2, Eye, EyeOff } from "lucide-react";

function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const { register, handleSubmit, watch, trigger, formState: { errors, isSubmitted } } = useForm();
  const password = watch("senha");

  // Revalida ao trocar de idioma para regenerar as mensagens de erro no idioma atual
  useEffect(() => {
    if (isSubmitted) trigger();
  }, [locale, isSubmitted, trigger]);

  async function handleRegister(data) {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, senha: data.senha }),
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success(t('toasts.registerSuccess'));
        navigate("/login");
      } else {
        toast.error(responseData.error || t('toasts.registerError'))
      }
    } catch (error) {
      console.error(error)
      toast.error(t('toasts.connectionError'))
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6">
      <div className="bg-[#111] border border-white/10 rounded-xl shadow-lg p-8 w-full max-w-md text-center">
        <h1 className="text-3xl font-bold text-white mb-6">{t('register.title')}</h1>
        <form onSubmit={handleSubmit(handleRegister)} className="space-y-4" noValidate>
          <div className="text-left">
            <input
              type="email"
              placeholder={t('register.emailPlaceholder')}
              {...register("email", emailValidation(t))}
              className={`w-full p-3 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                errors.email ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>
          <div className="text-left">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('register.passwordPlaceholder')}
                {...register("senha", passwordValidation(t))}
                className={`w-full p-3 pr-12 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                  errors.senha ? 'border-red-500' : 'border-white/10'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.senha && (
              <p className="text-red-500 text-sm mt-1">{errors.senha.message}</p>
            )}
          </div>
          <div className="text-left">
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t('validation.passwordConfirmRequired')}
                {...register("confirmPassword", passwordConfirmValidation(t, password))}
                className={`w-full p-3 pr-12 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-white/10'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-500 hover:bg-brand-400 text-black font-semibold py-3 px-4 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="animate-spin" size={20} />}
            {t('register.button')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Register;
